import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ModeSwitch } from "@/components/poetry/PoetryWidget";
import { ScriptBar } from "@/components/script/ScriptBar";
import { PageEditor } from "@/components/studio/PageEditor";
import { DesktopHint } from "@/components/studio/DesktopOnly";
import { isScript } from "@/lib/script";
import { fmtNum } from "@/lib/studio";

const STATE = { saved: "Saved", dirty: "Unsaved", saving: "Saving…", error: "Couldn't save", offline: "Saved on device" };

const NavBtn = ({ to, label, icon: Icon, testid }) => (to
  ? <Link to={to} className="flex h-9 w-9 items-center justify-center rounded-md text-[#F3EBDD] hover:bg-[#1A202C]" aria-label={label} data-testid={testid}><Icon className="h-5 w-5" /></Link>
  : <span className="flex h-9 w-9 items-center justify-center text-[#2D3442]"><Icon className="h-5 w-5" /></span>);

export const MobileChapter = ({ ch, cid, sid, base, chapterNo, title, onTitle, editable, state, mode, setMode, words, content, onContent, onEditor, editorObj, extensions, infoBtn, infoSheet }) => {
  const i = ch.chapters.findIndex((c) => c.id === cid);
  const link = (c) => c && `${base}/studio/${sid}/c/${c.id}`;
  return (
    <div className="px-3 pb-28 pt-3" data-testid="mobile-chapter-page">
      <div className="flex items-center gap-1">
        <Link to={`${base}/studio/${sid}`} className="flex min-w-0 flex-1 items-center gap-1 text-xs text-[#A8A090]" data-testid="back-to-story"><ChevronLeft className="h-4 w-4 shrink-0" /><span className="truncate">{ch.story.title}</span></Link>
        {editable && <span className="whitespace-nowrap text-[11px] text-[#A8A090]" data-testid="chapter-save-state">{STATE[state]}</span>}
        <NavBtn to={link(ch.chapters[i - 1])} label="Previous chapter" icon={ChevronLeft} testid="chapter-prev" />
        <NavBtn to={link(ch.chapters[i + 1])} label="Next chapter" icon={ChevronRight} testid="chapter-next" />
      </div>
      <div className="mt-2 font-display text-2xl text-[#F59E0B]" data-testid="chapter-number">Chapter {chapterNo}</div>
      {editable
        ? <Input value={title} maxLength={200} placeholder="Chapter name (optional)" onChange={(e) => onTitle(e.target.value)} className="mt-1 h-11 border-[#2D3442] bg-[#0F131A] font-display text-xl text-[#F3EBDD]" data-testid="chapter-title-input" />
        : title && <div className="font-display text-2xl text-[#F3EBDD] [overflow-wrap:anywhere]" data-testid="chapter-title-text">{title}</div>}
      <div className="mt-3 flex items-center gap-3 overflow-x-auto pb-1 text-xs text-[#A8A090]">
        <ModeSwitch mode={mode} setMode={setMode} disabled={!editable} />
        <span className="whitespace-nowrap" data-testid="chapter-word-count"><b className="text-[#F3EBDD]">{fmtNum(words)}</b> words</span>
        <span className="whitespace-nowrap">{infoBtn}</span>
      </div>
      {!editable && (
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-[#FBBF24]" data-testid="chapter-readonly-note">
          <span className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />{ch.locked ? `${ch.draft.name} is read-only.` : "Read-only view."}</span>
          <Link to={`/read/${sid}?c=${cid}`} className="flex items-center gap-1 text-[#F59E0B]" data-testid="mobile-chapter-reader-link"><BookOpen className="h-3.5 w-3.5" />Reader mode</Link>
        </div>
      )}
      <div className="mt-3">
        <PageEditor fluid value={content} onChange={onContent} disabled={!editable} onEditor={onEditor} extensions={extensions} extraTools={editable && isScript(mode) && <ScriptBar editor={editorObj} mode={mode} />} />
      </div>
      {editable && <div className="mt-4"><DesktopHint testid="mobile-chapter-desktop-hint">Widgets, lore check, live spelling check, footnotes and page layout open on desktop.</DesktopHint></div>}
      {infoSheet}
    </div>
  );
};
